import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { SERVER_BASE_URL } from '../livekit/endpoints';

function createBlackVideoTrack(width = 640, height = 480) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, width, height);

  const stream = canvas.captureStream(15);
  const track = stream.getVideoTracks()[0];
  track.enabled = true;
  return track;
}

// VSS server root for socket.io signalling + recording-download redirect.
// Env-driven (REACT_APP_TOKEN_API_URL via endpoints.js) so it tracks per environment
// (local / dev / prod) instead of a hardcoded host. Local falls back to :8080.
const serverUrl = SERVER_BASE_URL;


const useWebRTC = (roomId, nickname, localStream, { audioEnabled, videoEnabled }) => {
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [pinnedUserId, setPinnedUserId] = useState(null);
  const [screenSharingUserId, setScreenSharingUserId] = useState(null);
  const socketRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const dataChannelsRef = useRef({});
  const activeUserIds = useRef(new Set());
  const screenShareRef = useRef(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  const [localScreenStream, setLocalScreenStream] = useState(null);
  const [screenStreamId, setScreenStreamId] = useState(null);
  const screenSharingUserIdRef = useRef(null);
  const localScreenStreamRef = useRef(null);
  const isScreenSharingRef = useRef(null);
  const screenStreamIdRef = useRef(null);
  const pinnedUserIdRef = useRef(null);
  const mediaStatusRef = useRef({});
  const remoteUsersRef = useRef(remoteUsers);
  const [isRecording, setIsRecording] = useState(false);
  const recorderPeerRef = useRef(null);
  const pendingCandidatesRef = useRef({});
  const blackVideoTrackRef = useRef(null);




  useEffect(() => {
    screenSharingUserIdRef.current = screenSharingUserId;
  }, [screenSharingUserId]);

  useEffect(() => {
    isScreenSharingRef.current = isScreenSharing;
  }, [isScreenSharing]);

  useEffect(() => {
    screenStreamIdRef.current = screenStreamId;
  }, [screenStreamId]);

  useEffect(() => {
    screenStreamIdRef.current = screenStreamId;
  }, [screenStreamId]);

  useEffect(() => {
    localScreenStreamRef.current = localScreenStream;
  }, [localScreenStream]);

  useEffect(() => {
    console.log("Pinned user ID updated:", pinnedUserId);
    pinnedUserIdRef.current = pinnedUserId;
  }, [pinnedUserId]);

  useEffect(() => {
    remoteUsersRef.current = remoteUsers;
  }, [remoteUsers]);

  useEffect(() => {
    if (!blackVideoTrackRef.current) {
      blackVideoTrackRef.current = createBlackVideoTrack();
    }
  }, []);

  const cameraVideoTrackRef = useRef(null);

  useEffect(() => {
    if (localStream?.getVideoTracks().length) {
      cameraVideoTrackRef.current = localStream.getVideoTracks()[0];
    }
  }, [localStream]);



  useEffect(() => {
    if (!localStream) return; // Important guard
    const socket = io(serverUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 30, // = 3 min at 2s
      reconnectionDelay: 2000,
      timeout: 20000 // 20 seconds
    });
    socketRef.current = socket;


    const handleRoomInfo = (roomInfo) => {
      console.log('Received room info:', roomInfo);

      // Handle screen share status
      if (roomInfo.screenSharer) {
        console.log("Screen::: " + new Date().getTime() + " screenSharingUserId:" + roomInfo.screenSharer + " StreamId:: " + screenStreamId);
        setScreenSharingUserId(roomInfo.screenSharer);
      }

      // Connect to existing users
      if (roomInfo?.users) {
        Object.keys(roomInfo.users).forEach(userId => {
          const user = roomInfo.users[userId];
          if (
            userId &&
            userId !== socket.id &&
            user?.nickname !== 'Recorder Bot' &&  // 👈 skip recorder
            !peerConnectionsRef.current[userId]
          ) {
            createPeerConnection(userId, user.nickname, false);
          }
        });
      }
    };

    const handleScreenShareStarted = (userId) => {
      setScreenSharingUserId(userId);
      console.log("Screen::: " + new Date().getTime() + " screenSharingUserId:" + userId + " StreamId:: " + screenStreamId);
    };

    const handleScreenShareStopped = (userId) => {
      if (screenSharingUserId === userId) {
        setScreenSharingUserId(null);
      }
    };

    socket.on('connect', () => {
      console.log('Connected to signaling server with ID:', socket.id);
      socket.emit('join-room', roomId, socket.id, nickname, {
        audioEnabled: localStream?.getAudioTracks()[0]?.enabled || false,
        videoEnabled: videoEnabled || false
      });
      socket.emit('network-status', roomId, socket.id, 'good');
    });


    socket.on('room-info', handleRoomInfo);
    // socket.on('screen-share-started', handleScreenShareStarted);
    // socket.on('screen-share-stopped', handleScreenShareStopped);

    // not being used but can be useful for debugging
    socket.on('duplicate-user', (message) => {
      console.error('Duplicate user:', message);
    });

    socket.on('room-info', (roomInfo) => {
      console.log('Received room info:', roomInfo);
      if (!roomInfo?.users) return;

      Object.keys(roomInfo.users).forEach(userId => {
        const user = roomInfo.users[userId];
        if (
          userId &&
          userId !== socket.id &&
          user?.nickname !== 'Recorder Bot' &&   // 👈 skip recorder here too
          !peerConnectionsRef.current[userId]
        ) {
          createPeerConnection(userId, user.nickname, false);
        }
      });
    });

    socket.on('user-connected', async (userId, userNickname) => {
      console.log('User connected:', userId, userNickname);
      // Do NOT make a normal P2P connection to Recorder Bot
      if (
        userId &&
        userId !== socket.id &&
        userNickname !== 'Recorder Bot' &&   // 👈 guard
        !peerConnectionsRef.current[userId]
      ) {
        createPeerConnection(userId, userNickname, true);
      } else if (userNickname === 'Recorder Bot') {
        console.log('[Recorder] Skipping normal mesh connection to Recorder Bot');
      }
    });


    // socket.emit('join-room', roomId, socket.id, nickname, {  audioEnabled: localScreenStreamRef.current?.getAudioTracks()[0]?.enabled || false,
    //   videoEnabled: localScreenStreamRef.current?.getVideoTracks()[0]?.enabled || false
    // });


    socket.on('user-disconnected', (userId) => {
      if (peerConnectionsRef.current[userId]) {
        peerConnectionsRef.current[userId].close();
        delete peerConnectionsRef.current[userId];
        delete dataChannelsRef.current[userId];
      }
      setRemoteUsers(prev => prev.filter(user => user.userId !== userId));
      if (pinnedUserId === userId) setPinnedUserId(null);
      if (screenSharingUserId === userId) setScreenSharingUserId(null);
    });

    socket.on('signal', async (toId, fromId, signal, screenStreamIdType) => {
      if (!fromId) return;

      // =================== RECORDER-RELATED CANDIDATES ===================
      // When recorder sends ICE candidates to the host:
      //   server emits: signal(toId = hostSocketId, fromId = 'recorder', signal = { candidate }, ...)
      if (fromId === 'recorder' && signal && signal.candidate) {
        const recorderPeer = recorderPeerRef.current;
        const key = 'recorder';

        if (!recorderPeer || !recorderPeer.remoteDescription || !recorderPeer.remoteDescription.type) {
          // queue candidates until remoteDescription is set in recorder-answer handler
          pendingCandidatesRef.current[key] = pendingCandidatesRef.current[key] || [];
          pendingCandidatesRef.current[key].push(signal.candidate);
          console.log('🧊 Queued ICE candidate from recorder');
        } else {
          try {
            await recorderPeer.addIceCandidate(new RTCIceCandidate(signal.candidate));
            console.log('[Signal] Added ICE candidate from recorder');
          } catch (e) {
            console.warn('[Signal] Failed to add candidate from recorder', e);
          }
        }
        return; // important: do NOT fall through to normal peer logic
      }

      // =================== NORMAL PEER SIGNALS ===================
      // For normal mesh peers, only handle signals addressed to us
      if (toId !== socket.id) return;

      const pc = peerConnectionsRef.current[fromId];
      if (!pc) {
        console.warn('No peer connection for', fromId);
        return;
      }

      // Maintain screen stream ID mapping (unchanged logic)
      if (screenStreamIdType && screenStreamIdType !== 'video' && screenStreamIdType !== screenStreamIdRef.current) {
        screenStreamIdRef.current = screenStreamIdType;
        setScreenStreamId(screenStreamIdType);
      }

      try {
        if (signal.candidate) {
          // Queue ICE candidates if remote description isn't set yet
          if (!pc.remoteDescription || !pc.remoteDescription.type) {
            pendingCandidatesRef.current[fromId] = pendingCandidatesRef.current[fromId] || [];
            pendingCandidatesRef.current[fromId].push(signal.candidate);
            console.log('🧊 Queued ICE candidate for', fromId);
          } else {
            await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
            console.log('[Signal] Added ICE candidate from', fromId);
          }
        } else if (signal.type) {
          console.log('Setting remote description:', signal.type);
          await pc.setRemoteDescription(new RTCSessionDescription(signal));

          // Drain any queued candidates now that RD is set
          const queued = pendingCandidatesRef.current[fromId] || [];
          for (const c of queued) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(c));
              console.log("✅ Added queued ICE candidate from", fromId);
            } catch (e) {
              console.warn("⚠️ Failed to add queued ICE candidate:", e);
            }
          }
          pendingCandidatesRef.current[fromId] = [];

          if (signal.type === 'offer') {
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            socket.emit('signal', fromId, socket.id, answer, roomId);
          }
        }
      } catch (err) {
        console.error('Signal processing error:', err);
      }
    });


    socket.on('screen-share-started', (userId, streamId) => {
      setScreenSharingUserId(userId);
      setScreenStreamId(streamId);
      console.log("screen-share-started " + new Date().getTime() + " :: Screen::: screenSharingUserId:" + userId + " StreamId:: " + streamId);
    });

    socket.on('screen-share-stopped', (userId) => {
      setScreenSharingUserId(null);
    });

    // ===================== Recorder Events =====================
    socket.on('recording-status', (status) => {
      console.log('[Recording Status]', status);
      if (status === 'started') {
        console.log('Recording started');
        setIsRecording(true);
      } else if (status === 'stopped' || status === 'failed') {
        setIsRecording(false);
        console.log('Recording stopped');
        if (recorderPeerRef.current) {
          recorderPeerRef.current.close();
          recorderPeerRef.current = null;
        }
      }
    });

    const RECORDER_CONN_TIMEOUT_MS = 30 * 1000; // 30s (increase if needed)

    socket.on('prepare-recorder', async ({ roomId }) => {
      console.log('[Recorder] prepare-recorder received');

      if (!recorderPeerRef.current) {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });

        recorderPeerRef.current = pc;

        pc.onicecandidate = (e) => {
          if (e.candidate) {
            socket.emit(
              'signal',
              'recorder',
              socket.id,
              { candidate: e.candidate },
              roomId
            );
          }
        };

        pc.onconnectionstatechange = () => {
          console.log('[Recorder PC] state:', pc.connectionState);
        };
      }

      const pc = recorderPeerRef.current;

      // 🔹 ADD ALL CURRENT TRACKS (camera + mic + screen if any)
      addTracksToRecorder(pc);

      // 🔹 CREATE OFFER
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // const videoEnabled = localStream?.getVideoTracks()[0]?.enabled;

      socket.emit('recorder-offer', {
        roomId,
        fromId: socket.id,
        sdp: offer,
        streamType: localScreenStreamRef.current ? 'screen' : 'video',
        streamId: localScreenStreamRef.current
          ? localScreenStreamRef.current.id
          : localStream?.id,
        displayName: nickname,
        videoEnabled: videoEnabled || false
      });

      console.log('[Recorder] offer sent');
      // Setup timeout to clean up if recorder doesn't respond
      setTimeout(() => {
        if (recorderPeerRef.current && recorderPeerRef.current.connectionState !== 'connected') {
          console.warn('[Recorder] connection timeout, closing recorder peer');
          recorderPeerRef.current.close();
          recorderPeerRef.current = null;
          setIsRecording(false);
        }
      }, RECORDER_CONN_TIMEOUT_MS);
    });




    // Recorder's answer arrives — now includes recorderFromId so we can drain queued ICE candidates
    // Recorder's answer arrives — expects { sdp, fromId }
    socket.on('recorder-answer', async ({ sdp, fromId: recorderFromId } = {}) => {
      console.log('[Recorder] received answer from recorder:', recorderFromId);

      const recorderPeer = recorderPeerRef.current;
      if (!recorderPeer) {
        console.warn('[Recorder] No recorder peer exists to set remote description. Ignoring answer for now.');
        return;
      }

      if (!sdp) {
        console.warn('[Recorder] recorder-answer has no sdp; ignoring');
        return;
      }

      try {
        await recorderPeer.setRemoteDescription(new RTCSessionDescription(sdp));
        console.log('[Recorder] setRemoteDescription for recorder');

        // Drain any queued ICE candidates that came earlier and were stored under recorderFromId
        if (recorderFromId) {
          const key = 'recorder';
          const queued = pendingCandidatesRef.current[key] || [];
          if (queued.length) {
            console.log(`[Recorder] draining ${queued.length} queued candidates from recorder`);
            for (const cand of queued) {
              try {
                await recorderPeer.addIceCandidate(new RTCIceCandidate(cand));
              } catch (err) {
                console.warn('[Recorder] failed to add queued candidate', err);
              }
            }
            pendingCandidatesRef.current[key] = [];
          }
        }

        console.log('[Recorder] Recorder Bot connected!');
      } catch (err) {
        console.error('[Recorder] error setting remote description', err);
      }
    });



    socket.on('recorder-stopped', () => {
      console.log('[Recorder] stopped');
      if (recorderPeerRef.current) {
        recorderPeerRef.current.close();
        recorderPeerRef.current = null;
      }
      setIsRecording(false);
    });


    // Add these socket event handlers in your useEffect hook:
    socketRef.current.on('media-status', (userId, audioEnabled, videoEnabled) => {
      console.log("Received media status for user:", userId, "Audio enabled:", audioEnabled, "Video enabled:", videoEnabled,
        remoteUsersRef.current
      );
      if (!userId) {
        console.warn('Invalid media status data:', userId);
        return;
      }
      if (!remoteUsersRef.current.some(user => user.userId === userId)) {
        mediaStatusRef.current[userId] = {
          audioEnabled,
          videoEnabled
        };
        return;
      }
      console.log("mediaStatusRef before update:", mediaStatusRef.current);
      delete mediaStatusRef.current[userId];

      setRemoteUsers(prev => prev.map(user =>
        user.userId === userId ? { ...user, audioEnabled, videoEnabled } : user
      ));
      console.log("Updated media status for :", remoteUsers);
    });

    socketRef.current.on('speaking-status', (userId, isSpeaking) => {
      setRemoteUsers(prev => prev.map(user =>
        user.userId === userId ? { ...user, isSpeaking } : user
      ));
    });

    socketRef.current.on('network-status', (userId, strength) => {
      setRemoteUsers(prev => prev.map(user =>
        user.userId === userId ? { ...user, networkStrength: strength } : user
      ));
    });

    socketRef.current.on('chat-message', (userId, nickname, message) => {
      if (userId === socketRef.current.id) {
        // If the message is from the local user, we can skip adding it again
        return;
      }
      console.log("Received chat message from", userId, nickname, message);
      setChatMessages(prev => [...prev, { userId, nickname, message }]);
    });

    // Listen for raise-hand broadcasts
    socketRef.current.on('raise-hand-broadcast', (userId, isRaised, userNickname) => {
      console.log('Received raise-hand event:', userId, isRaised, userNickname);
      // This will be handled in the Room component
    });

    socket.on('recording-download', ({ url, filename, fileId }) => {
      // console.log('[Recording] download available:', url, filename);
      // setTimeout(() => {
      //   const a = document.createElement('a');
      //   a.href = "http://localhost:8080" + url;
      //   a.download = filename;
      //   document.body.appendChild(a);
      //   a.click();
      //   document.body.removeChild(a);
      //   console.log('[Recording] download triggered for', filename);
      //   // ACK back to server
      //   socket.emit('recording-downloaded', { filename });
      // }, 300); // 300ms is enough
      setTimeout(() => {
        window.location.href = serverUrl + url;;
      }, 500);
      console.log('[Recording] download triggered for', filename);
      socket.emit('recording-downloaded', { filename });
    });


    return () => {
      socket.off('room-info', handleRoomInfo);
      socket.off('screen-share-started', handleScreenShareStarted);
      socket.off('screen-share-stopped', handleScreenShareStopped);
      socket.off('user-connected');
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const peerConnections = peerConnectionsRef.current;
      Object.values(peerConnections).forEach(pc => pc.close());
      socket.disconnect();

    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, nickname]);


  const addTracksToRecorder = (pc) => {
    // camera + mic
    if (localStream) {
      localStream.getTracks().forEach(track => {
        const alreadyAdded = pc.getSenders().some(s => s.track === track);
        if (!alreadyAdded) {
          pc.addTrack(track, localStream);
        }
      });
    }

    // screen share (if active)
    if (localScreenStreamRef.current) {
      localScreenStreamRef.current.getTracks().forEach(track => {
        const alreadyAdded = pc.getSenders().some(s => s.track === track);
        if (!alreadyAdded) {
          pc.addTrack(track, localScreenStreamRef.current);
        }
      });
    }
  };


  const handleScreenShare = async () => {
    if (isScreenSharing) {
      stopScreenShare();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "browser",
          frameRate: 20,
        },
        audio: false
      });

      // Set state consistently
      setLocalScreenStream(stream);
      screenStreamRef.current = stream;
      setIsScreenSharing(true);
      setScreenSharingUserId(socketRef.current.id); // Use socket ID consistently
      console.log("handleScreenShare:: Screen::: " + new Date().getTime() + " screenSharingUserId:" + socketRef.current.id + " StreamId:: " + screenStreamId);

      socketRef.current.emit('start-screen-share', roomId, socketRef.current.id, stream.id, () => {

        console.log("screenshare screen streamId:", stream.id);
        // Replace tracks
        Object.entries(peerConnectionsRef.current).forEach(([userId, pc]) => {
          const videoSender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (videoSender) {
            Object.entries(peerConnectionsRef.current).forEach(([userId, pc]) => {
              const screenTrack = stream.getVideoTracks()[0];

              try {
                pc.addTrack(screenTrack, stream);
                pc.createOffer()
                  .then(offer => {
                    return pc.setLocalDescription(offer).then(() => {
                      socketRef.current.emit('signal', userId, socketRef.current.id, offer, roomId);
                      console.log("📨 Sent offer with screen track to", userId);
                    });
                  })
                  .catch(err => {
                    console.error("❌ Error renegotiating:", err);
                  });
              } catch (err) {
                console.error('Error adding screen track:', err);
              }
            });
          }
        });
      });

      // 🔴 NEW: also add the screen track to Recorder Bot if recording is active
      if (recorderPeerRef.current) {
        try {
          const pc = recorderPeerRef.current;

          addTracksToRecorder(pc);

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);

          socketRef.current.emit('recorder-offer', {
            roomId,
            fromId: socketRef.current.id,
            sdp: offer,
            streamType: 'screen',
            streamId: stream.id,
            displayName: nickname,
            videoEnabled: videoEnabled || false
          });

          console.log('[Recorder] screen renegotiation sent');

        } catch (err) {
          console.error('[Recorder] error adding screen share to recorder', err);
        }
      }


      stream.getVideoTracks()[0].onended = stopScreenShare;
    } catch (error) {
      console.error('Screen share error:', error);
    }
  };

  const stopScreenShare = useCallback(() => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      setLocalScreenStream(null);
      screenStreamRef.current = null;
      setIsScreenSharing(false);
      setScreenSharingUserId(null); // Clear both states

      // Notify server
      socketRef.current.emit('stop-screen-share', roomId, socketRef.current.id, screenStreamIdRef.current);
      setScreenSharingUserId(null);


      // Restore camera tracks if available
      if (localStream) {
        Object.entries(peerConnectionsRef.current).forEach(([userId, pc]) => {
          // Find and replace the video track instead of adding new one
          const videoSender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (videoSender && localStream.getVideoTracks().length > 0) {
            videoSender.replaceTrack(localStream.getVideoTracks()[0])
              .catch(err => console.error('Error replacing video track:', err));
          }

          // For audio, just enable the existing track if needed
          const audioSender = pc.getSenders().find(s => s.track?.kind === 'audio');
          if (audioSender && localStream.getAudioTracks().length > 0) {
            audioSender.replaceTrack(localStream.getAudioTracks()[0])
              .catch(err => console.error('Error replacing audio track:', err));
          }
        });
      }
      // 🔥 IMPORTANT: remove screen track from Recorder PC
      if (recorderPeerRef.current) {
        const pc = recorderPeerRef.current;

        const screenSenders = pc.getSenders().filter(
          s => s.track && s.track.kind === 'video' && s.track.label.includes('screen')
        );

        screenSenders.forEach(sender => {
          try {
            pc.removeTrack(sender);
            console.log('[Recorder] removed screen sender from recorder PC');
          } catch (e) {
            console.warn('[Recorder] failed to remove screen sender', e);
          }
        });

        // 🔁 Force renegotiation
        pc.createOffer()
          .then(offer => pc.setLocalDescription(offer))
          .then(() => {
            socketRef.current.emit('recorder-offer', {
              roomId,
              fromId: socketRef.current.id,
              sdp: pc.localDescription,
              streamType: localScreenStreamRef.current ? 'screen' : 'video',
              streamId: localStream?.id,
              displayName: nickname,
              videoEnabled: videoEnabled || false
            });
            console.log('[Recorder] renegotiated after screen stop');
          })
          .catch(err => console.error('[Recorder] renegotiation failed', err));
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStream, roomId]);

  // Handle screen share events from server
  useEffect(() => {
    const handleScreenShareStarted = (userId) => {
      setScreenSharingUserId(userId);
      console.log("handleScreenShareStarted:: Screen::: " + new Date().getTime() + " screenSharingUserId:" + userId + " StreamId:: " + screenStreamId);

      // If it's our own screen share, ensure we have the stream
      if (userId === socketRef.current?.id && !localScreenStream) {
        // This handles cases where the UI triggered the share
        if (screenShareRef.current) {
          setLocalScreenStream(screenShareRef.current);
        }
      }
    };

    socketRef.current?.on('screen-share-started', handleScreenShareStarted);

    return () => {
      socketRef.current?.off('screen-share-started', handleScreenShareStarted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localScreenStream]);

  const cleanupPeerConnection = (userId) => {
    if (peerConnectionsRef.current[userId]) {
      peerConnectionsRef.current[userId].close();
      delete peerConnectionsRef.current[userId];
    }
    activeUserIds.current.delete(userId);
    setRemoteUsers(prev => prev.filter(u => u.userId !== userId));
  };

  const createPeerConnection = async (userId, userNickname, isInitiator) => {
    let connectionTimeout = null;
    try {
      if (!userId || !userNickname || activeUserIds.current.has(userId)) {
        console.error('Invalid or duplicate connection attempt:', userId);
        return null;
      }
      activeUserIds.current.add(userId);
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' }
        ],
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require'
      });

      peerConnectionsRef.current[userId] = pc;
      pendingCandidatesRef.current[userId] = pendingCandidatesRef.current[userId] || [];

      if (userId !== 'recorder') {
        // Add timeout for connection establishment
        connectionTimeout = setTimeout(() => {
          if (pc.iceConnectionState !== 'connected') {
            console.warn('Connection timeout with', userId);
            cleanupPeerConnection(userId);
          }
        }, 20000); // 15 second timeout
      }
      else {
        console.log('[Recorder] Skipping connection timeout cleanup for recorder');
      }


      // ICE candidate handling
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socketRef.current.emit('signal', userId, socketRef.current.id, {
            candidate: event.candidate
          }, roomId);
        }
      };

      // Connection state handling
      pc.onconnectionstatechange = () => {
        console.log(`Connection state with ${userId}:`, pc.connectionState);
        if (pc.connectionState === 'connected') {
          clearTimeout(connectionTimeout);
        }
        if (pc.connectionState === 'failed') {
          if (userId !== 'recorder') cleanupPeerConnection(userId);
          // cleanupPeerConnection(userId);
        }
      };

      pc.onicecandidateerror = (event) => {
        console.error('ICE candidate error:', {
          address: event.address,
          port: event.port,
          url: event.url,
          errorCode: event.errorCode,
          errorText: event.errorText
        });
      };

      pc.oniceconnectionstatechange = () => {
        console.log(`ICE state with ${userId}:`, pc.iceConnectionState);
        if (pc.iceConnectionState === 'failed') {
          cleanupPeerConnection(userId);
        }
      };



      // Add network monitoring
      setInterval(() => {
        if (pc && pc.iceConnectionState) {
          console.log(`Current connection state with ${userId}:`,
            pc.iceConnectionState,
            'Peer connection state:',
            pc.connectionState
          );
        }
      }, 5001);

      pc.ontrack = (event) => {
        console.log(`Received track from ${userId}`, event.streams);

        if (!event.streams || event.streams.length === 0) return;


        const stream = event.streams[0];
        if (!stream) return;

        const videoTrack = stream.getVideoTracks()[0];

        if (videoTrack) {
          console.log("Video pixels: ", videoTrack.getSettings()); // Log video track settings
        }


        const isScreenShare =
          stream.id === screenStreamIdRef.current ||
          (userId === screenSharingUserIdRef.current &&
            screenStreamIdRef.current &&
            stream.id === screenStreamIdRef.current);

        console.log("ontrack:: Screen::: " + new Date().getTime() + " screenSharingUserId:"
          + screenSharingUserIdRef.current + "  userId: " + userId + " current StreamId:: " + stream.id +
          "screenStreamIdRef " + screenStreamIdRef.current
        );

        console.log(`ontrack from ${userId}:`, {
          streamId: stream.id,
          trackLabel: videoTrack?.label,
          isScreenShare
        });
        console.log("Tracks received::", stream.getTracks(), isScreenShare, stream.id);

        setRemoteUsers(prev => {
          const existing = prev.find(u => u.userId === userId);
          let audioEnabled = stream.getAudioTracks().some(t => t.enabled);
          let videoEnabled = stream.getVideoTracks().some(t => t.enabled);

          // if mediaStatusRef?.current[userId]?.audioEnabled has value, use it, otherwise check the stream's audio track
          // before connecting to the peer connection and getting the tracks getting media status so storing the media status
          // in mediaStatusRef.current[userId] to avoid unnecessary checks
          if (mediaStatusRef.current[userId]) {
            audioEnabled = mediaStatusRef?.current[userId]?.audioEnabled;
            videoEnabled = mediaStatusRef?.current[userId]?.videoEnabled;
          }
          console.log("Media Status before update:", audioEnabled, videoEnabled, userId);
          const newUserData = {
            userId,
            nickname: userNickname,
            isSpeaking: false,
            audioEnabled,
            videoEnabled,
            networkStrength: 'good',
          };

          if (isScreenShare) {
            return existing
              ? prev.map(u => u.userId === userId
                ? { ...u, screenStream: stream }
                : u)
              : [...prev, { ...newUserData, stream: null, screenStream: stream }];
          } else {
            return existing
              ? prev.map(u => u.userId === userId
                ? { ...u, stream: stream }
                : u)
              : [...prev, { ...newUserData, stream: stream, screenStream: null }];
          }
        });

        if (isScreenShare) {
          setScreenSharingUserId(userId);
        }
      };

      // Add local tracks
      if (localStream) {
        localStream.getTracks().forEach(track => {
          try {
            pc.addTrack(track, localStream);
            console.log("Added local tracks");
            // // After creating new peer connection for the new user
            if (isScreenSharingRef.current && localScreenStreamRef.current) {
              const screenTrack = localScreenStreamRef.current.getVideoTracks()[0];
              pc.addTrack(screenTrack, localScreenStreamRef.current);
              console.log("Added screen track");

            }
          } catch (err) {
            console.error('Failed to add track:', err);
          }
        });
      }

      // Create offer if initiator
      if (isInitiator) {
        try {
          const offer = await pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true
          });
          await pc.setLocalDescription(offer);
          socketRef.current.emit('signal', userId, socketRef.current.id, offer, roomId, screenStreamIdRef.current);
          console.log("🔁 Sent offer to late joiner with all local streams", userId, pc);
          // sendMediaStatus();
        } catch (err) {
          console.error('Offer creation failed:', err);
          cleanupPeerConnection(userId);
        }
      }
    } catch (err) {
      console.error('Error creating peer connection:', err);
    }
    return () => {
      clearTimeout(connectionTimeout); // Important cleanup
    };
  };

  const sendMediaStatus = () => {
    socketRef.current.emit('media-status',
      roomId,
      socketRef.current.id,
      audioEnabled,
      videoEnabled
    );
  };

  // Voice detection 
  // This will emit speaking status every second
  // It uses AudioContext to analyze the audio stream and detect if the user is speaking
  useEffect(() => {
    if (!localStream) return;

    const detectSpeaking = (stream, onSpeakingChange) => {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 32;
      analyser.smoothingTimeConstant = 0.2;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let prevSpeaking = false;
      let frameId = null;

      const checkSpeaking = () => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i += 2) {
          sum += dataArray[i];
        }
        const avg = sum / (dataArray.length / 2);
        const isSpeaking = avg > 10;

        if (isSpeaking !== prevSpeaking) {
          prevSpeaking = isSpeaking;
          onSpeakingChange(isSpeaking);
        }
        frameId = requestAnimationFrame(checkSpeaking);
      };

      const timeoutId = setTimeout(() => {
        frameId = requestAnimationFrame(checkSpeaking);
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
        if (frameId) cancelAnimationFrame(frameId);
        try {
          analyser.disconnect();
          source.disconnect();
          if (audioContext.state !== 'closed') audioContext.close();
        } catch (e) {
          console.warn('Audio cleanup error:', e);
        }
      };
    };

    const cleanup = detectSpeaking(localStream, (isSpeaking) => {
      socketRef.current.emit('speaking-status', roomId, socketRef.current.id, isSpeaking);
    });

    return cleanup;
  }, [localStream, roomId]);

  // Network status monitoring
  // This will send network status every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      Object.entries(peerConnectionsRef.current).forEach(([userId, pc]) => {
        const strength =
          pc.connectionState === 'connected' ? 'good' :
            pc.connectionState === 'connecting' ? 'poor' : 'bad';

        socketRef.current.emit('network-status', roomId, userId, strength);
      });
    }, 5001);

    return () => clearInterval(interval);
  }, [roomId]);

  // const audioEnabled = localStream?.getAudioTracks()[0]?.enabled;
  // const videoEnabled = localStream?.getVideoTracks()[0]?.enabled;

  useEffect(() => {
    if (!recorderPeerRef.current) return;
    if (!cameraVideoTrackRef.current) return;

    socketRef.current.emit('recorder-intent', {
      roomId,
      streamId: localStream.id,
      videoEnabled
    });

    const sender = recorderPeerRef.current
      .getSenders()
      .find(s => s.track?.kind === 'video');

    if (!sender) return;

    if (videoEnabled) {
      // Camera ON → real frames
      console.log("Replacing recorder video track with camera track");
      sender.replaceTrack(cameraVideoTrackRef.current);
    } else {
      console.log("Replacing recorder video track with black frames");
      // Camera OFF → black frames
      sender.replaceTrack(blackVideoTrackRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoEnabled]);

  useEffect(() => {
    if (!socketRef.current) return;

    sendMediaStatus();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioEnabled, videoEnabled]);

  // Call this whenever media status changes

  const handlePinUser = (userId) => {
    const newPinnedUserId = userId === pinnedUserId ? null : userId;
    setPinnedUserId(newPinnedUserId);
  };

  const sendChatMessage = (message) => {
    socketRef.current.emit('chat-message',
      roomId,
      socketRef.current.id,
      nickname,
      message
    );
    setChatMessages(prev => [...prev, {
      userId: socketRef.current.id,
      nickname,
      message
    }]);
  };

  const startRecording = useCallback(() => {
    if (!socketRef.current || isRecording) return;
    socketRef.current.emit('start-recording', roomId, socketRef.current.id);
  }, [roomId, isRecording]);

  const stopRecording = useCallback(() => {
    if (!socketRef.current || !isRecording) return;
    socketRef.current.emit('stop-recording', roomId);
  }, [roomId, isRecording]);



  const leaveRoom = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    Object.values(peerConnectionsRef.current).forEach(pc => {
      const senders = pc.getSenders();
      for (const sender of senders) {
        if (sender.track?.kind === 'video' && sender.track.label.includes('screen')) {
          pc.removeTrack(sender);
        }
      }
      pc.close();
    });
    socketRef.current.disconnect();
    localStorage.clear();
    // setJoined(false);
  };

  useEffect(() => {
    const socket = socketRef.current;

    if (!socket) return;

    const handleForceStop = () => {
      console.log("Received force-stop-screen-share from server");
      stopScreenShare();
      alert("You were removed from screen sharing because someone else started sharing.");
    };

    socket.on("force-stop-screen-share", handleForceStop);

    return () => {
      socket.off("force-stop-screen-share", handleForceStop);
    };
  }, [stopScreenShare]);

  useEffect(() => {
    const handleUnload = () => {
      socketRef.current.emit("user-leaving", { roomId, userId: socketRef.current.id });
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [roomId]);



  return {
    remoteUsers,
    pinnedUserId: pinnedUserId,
    screenSharingUserId,
    chatMessages,
    isScreenSharing: screenSharingUserIdRef.current !== null,
    // Derived screen-share UI state
    isScreenShareActive: screenSharingUserIdRef.current !== null,
    sharedStream: (() => {
      if (screenSharingUserIdRef.current === socketRef.current?.id) {
        return localScreenStream || null;
      }
      const sharer = remoteUsersRef.current.find(u => u.userId === screenSharingUserIdRef.current);
      return sharer?.screenStream || null;
    })(),
    thumbnailParticipants: (() => {
      const thumbnails = [];
      // Include local camera with nickname
      if (localStream) thumbnails.push({ id: socketRef.current?.id || 'local', stream: localStream, nickname });
      // Include all remote camera streams with their nicknames
      remoteUsersRef.current.forEach(u => {
        if (u.stream) thumbnails.push({ id: u.userId, stream: u.stream, nickname: u.nickname });
      });
      return thumbnails;
    })(),
    handleScreenShare,
    handlePinUser,
    sendChatMessage,
    leaveRoom,
    stopScreenShare,
    localScreenStream,
    socketRef,
    startRecording,
    stopRecording,
    isRecording
  };
};

export default useWebRTC;